/* eslint-disable react/default-props-match-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { useOptimizelyTrack } from 'reaxl-optimizely';

// Components
import { Modal, ModalBody, ModalHeader, ModalTitle } from 'reaxl';
import { callToActionAnalytics } from '@/analytics';
import BrowseByStyle from './BrowseByStyle';

const getLowercaseListingType = (listingType) => (typeof listingType === 'string' ? listingType.toLowerCase() : '');

const getConditionLink = (listingType) => {
    switch (getLowercaseListingType(listingType)) {
        case 'new':
            return '/new-cars';
        case 'certified':
            return '/certified-cars';
        default:
            return '';
    }
};

const getModalLabel = (listingType) => {
    switch (getLowercaseListingType(listingType)) {
        case 'new':
            return 'Shop New Vehicles by Style';
        case 'certified':
            return 'Shop Certified Vehicles by Style';
        default:
            return 'Shop Used Vehicles by Style';
    }
};

const getStyleLabel = (listingType, displayName) => {
    switch (getLowercaseListingType(listingType)) {
        case 'new':
            return `New ${displayName}s`;
        case 'certified':
            return `Certified ${displayName}s`;
        default:
            return `Used ${displayName}s`;
    }
};

const PATTERN_TRAILING_SLASH = /\/$/;

const getStyles = (listingType, styleCodes) => {
    const bodyStyles = [];
    const vehicleStyles = [];
    const conditionLink = getConditionLink(listingType);
    let style;

    Object.keys(styleCodes)
        .forEach((key) => {
            const {
                src,
                type,
                link,
                displayName,
            } = styleCodes[key];

            let srpLink = `/cars-for-sale${conditionLink}${link}`.toLowerCase();

            if (srpLink.match(PATTERN_TRAILING_SLASH) !== null) {
                const theLastIndex = srpLink.length - 1;
                srpLink = srpLink.substring(0, theLastIndex);
            }

            style = {
                src,
                href: srpLink,
                label: getStyleLabel(listingType, displayName),
                lazyload: false,
                displayName,
                rel: 'nofollow',
                alt: `Browse ${displayName} inventory near you`,
            };
            if (type === 'bodyStyle') {
                bodyStyles.push(style);
            } else {
                vehicleStyles.push(style);
            }

        });

    return { bodyStyles, vehicleStyles };
};

const handleStyleClick = (event, label, optimizelyTrack) => {
    callToActionAnalytics.fireBrowseByStyle(event, { co_txt_url: label, styleSelection: true });
    optimizelyTrack('hp_to_srp_conversion');
};

function BrowseByStylesModal({
    classNames,
    showModal,
    showHideModalToggleCallback = () => { },
    listingType,
    styleCodes = {},
    ...rest
}) {
    const optimizelyTrack = useOptimizelyTrack();

    const {
        bodyStyles,
        vehicleStyles,
    } = getStyles(listingType, styleCodes);

    const handleStyleClickCallback = (event, item) => {
        const label = item.displayName || 'browse-by-style';
        handleStyleClick(event, label, optimizelyTrack);
    };

    return !!bodyStyles.length && (
        <Modal
            dialogClassName="browseByStylesModal"
            show={showModal}
            onHide={showHideModalToggleCallback}
            bsSize="large"
            eventId="browse-style"
        >
            <ModalHeader closeButton>
                <ModalTitle className="text-center text-size-600">
                    {getModalLabel(listingType)}
                </ModalTitle>
            </ModalHeader>
            <ModalBody>
                <BrowseByStyle
                    className={classNames}
                    bodyStyles={bodyStyles}
                    vehicleStyles={vehicleStyles}
                    vehicleStylesAlign="center"
                    onClick={handleStyleClickCallback}
                    {...rest}
                />
            </ModalBody>
        </Modal>
    );
}

BrowseByStylesModal.propTypes = {
    classNames: PropTypes.string,
    showModal: PropTypes.bool,
    showHideModalToggleCallback: PropTypes.func,
    listingType: PropTypes.string,
};

export default BrowseByStylesModal;
